// src/screens/KidsScreen.js
//
// Parent-facing section for a child's room. Uses the same AudioEngine as the
// main Sleep screen so kids sounds mix properly, but the track list, timer,
// and imported audio are completely separate — nothing changed here affects
// the adult Sleep tab.
//
// Custom tracks imported on this screen are stored under their own AsyncStorage
// key so they don't mix with the user's personal music library.

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, Pressable,
  StyleSheet, ActivityIndicator, Alert, ImageBackground,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as DocumentPicker from 'expo-document-picker';
import { Ionicons } from '@expo/vector-icons';
import SoundTile from '../components/SoundTile';
import { KIDS_SOUNDS, KIDS_TIMER_PRESETS } from '../data/sounds';
import { AudioEngine } from '../audio/AudioEngine';
import { Colors, Radius, Typography, Fonts, useFontScale } from '../theme';

const KIDS_TRACKS_KEY = 'sleepmix.kids-tracks.v1';

export default function KidsScreen() {
  const fontScale = useFontScale();
  const [selectedMinutes, setSelectedMinutes] = useState(20);
  const [timerActive, setTimerActive]         = useState(false);
  const [remainingSec, setRemainingSec]       = useState(null);
  const [customTracks, setCustomTracks]       = useState([]);
  const [importing, setImporting]             = useState(false);

  // ── Load persisted tracks ─────────────────────────────────────────────────
  useEffect(() => {
    AsyncStorage.getItem(KIDS_TRACKS_KEY).then((raw) => {
      if (raw) setCustomTracks(JSON.parse(raw));
    });
  }, []);

  const saveTracks = useCallback((next) => {
    AsyncStorage.setItem(KIDS_TRACKS_KEY, JSON.stringify(next));
  }, []);

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'audio/*',
        copyToCacheDirectory: true,
      });
      if (!result.canceled) {
        const file = result.assets[0];
        const track = {
          id:   `kids-${Date.now()}-${file.name}`,
          uri:  file.uri,
          name: file.name.replace(/\.[^/.]+$/, ''),
        };
        setCustomTracks((prev) => {
          const next = [...prev, track];
          saveTracks(next);
          return next;
        });
      }
    } finally {
      setImporting(false);
    }
  };

  // ── Remove ────────────────────────────────────────────────────────────────
  const handleRemove = useCallback((track) => {
    Alert.alert(
      'Remove track',
      `Remove "${track.name}" from Kids sounds?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            AudioEngine.stopLayer(track.id).catch(() => {});
            setCustomTracks((prev) => {
              const next = prev.filter((t) => t.id !== track.id);
              saveTracks(next);
              return next;
            });
          },
        },
      ]
    );
  }, [saveTracks]);

  // ── Timer ─────────────────────────────────────────────────────────────────
  const startTimer = () => {
    AudioEngine.startSleepTimer(
      selectedMinutes,
      Math.min(5, Math.round(selectedMinutes * 0.2)),
      (secLeft) => setRemainingSec(secLeft),
      () => { setTimerActive(false); setRemainingSec(null); }
    );
    setTimerActive(true);
  };

  const cancelTimer = () => {
    AudioEngine.cancelSleepTimer();
    setTimerActive(false);
    setRemainingSec(null);
  };

  const formatTime = (sec) => {
    if (sec == null) return '';
    return `${Math.floor(sec / 60)}m left`;
  };

  return (
    <ImageBackground
      source={require('../../assets/backgrounds/bg_kids.jpg')}
      style={styles.screen}
      imageStyle={styles.bgImage}
    >
    <ScrollView style={{ flex: 1 }} contentContainerStyle={styles.content}>

      {/* ── Page intro ──────────────────────────────────────────────────── */}
      <Text style={styles.subheader}>
        Gentle sounds for a child's room — separate from your own Sleep mix.
      </Text>

      {/* ── Built-in sounds ─────────────────────────────────────────────── */}
      <View style={styles.sectionRow}>
        <View style={styles.sectionLabelGroup}>
          <Ionicons name="moon" size={12} color={Colors.kidsAccent} style={styles.sectionIcon} />
          <Text style={styles.sectionLabel}>Built-in sounds</Text>
        </View>
      </View>

      {KIDS_SOUNDS.map((s) => (
        <SoundTile key={s.id} id={s.id} name={s.name} uri={s.uri} icon={s.icon} />
      ))}

      {/* ── Custom / imported sounds ─────────────────────────────────────── */}
      <View style={styles.sectionRow}>
        <View style={styles.sectionLabelGroup}>
          <Ionicons name="headset" size={12} color={Colors.kidsAccent} style={styles.sectionIcon} />
          <Text style={styles.sectionLabel}>Your sounds</Text>
        </View>
        <Pressable style={styles.importButton} onPress={handleImport} disabled={importing}>
          {importing ? (
            <ActivityIndicator size="small" color={Colors.bg} />
          ) : (
            <>
              <Ionicons name="add" size={14} color={Colors.bg} />
              <Text style={styles.importText}>Import</Text>
            </>
          )}
        </Pressable>
      </View>

      {customTracks.length === 0 ? (
        <View style={styles.emptyCustom}>
          <View style={styles.emptyIconCircle}>
            <Ionicons name="musical-notes-outline" size={26} color={Colors.kidsAccent} />
          </View>
          <Text style={styles.emptyTitle}>No custom sounds yet</Text>
          <Text style={styles.emptyBody}>
            Tap Import to add your own audio — lullabies, white noise, anything you like — and it'll mix right alongside the built-in sounds.
          </Text>
        </View>
      ) : (
        customTracks.map((t) => (
          <SoundTile
            key={t.id}
            id={t.id}
            name={t.name}
            uri={t.uri}
            icon="musical-notes"
            onRemove={() => handleRemove(t)}
          />
        ))
      )}

      {/* ── Bedtime timer ───────────────────────────────────────────────── */}
      <View style={styles.timerCard}>
        <View style={styles.timerHeaderRow}>
          <Ionicons name="time-outline" size={16 * fontScale} color={Colors.kidsAccent} />
          <Text allowFontScaling={false} style={[styles.timerTitle, { fontSize: 16 * fontScale }]}>Bedtime timer</Text>
        </View>
        <Text style={styles.timerSub}>
          Fades out gently at the end so the room stays quiet.
        </Text>

        <View style={styles.presetRow}>
          {KIDS_TIMER_PRESETS.map((m) => (
            <Pressable
              key={m}
              onPress={() => setSelectedMinutes(m)}
              style={[styles.presetPill, selectedMinutes === m && styles.presetPillActive]}
            >
              <Text
                allowFontScaling={false}
                style={[
                  styles.presetText,
                  selectedMinutes === m && styles.presetTextActive,
                  { fontSize: 13 * fontScale },
                ]}
              >
                {m}m
              </Text>
            </Pressable>
          ))}
        </View>

        {timerActive ? (
          <Pressable style={[styles.actionButton, styles.cancelButton, { paddingVertical: Math.max(14, Math.round(14 * fontScale)) }]} onPress={cancelTimer}>
            <Text
              allowFontScaling={false}
              style={[styles.actionText, styles.cancelText, { fontSize: 15 * fontScale }]}
            >
              Cancel · {formatTime(remainingSec)}
            </Text>
          </Pressable>
        ) : (
          <Pressable style={[styles.actionButton, { paddingVertical: Math.max(14, Math.round(14 * fontScale)) }]} onPress={startTimer}>
            <Text
              allowFontScaling={false}
              style={[styles.actionText, { fontSize: 15 * fontScale }]}
            >
              Start {selectedMinutes}-minute timer
            </Text>
          </Pressable>
        )}
      </View>

      <Text style={styles.note}>
        Tip: sounds and timer here are separate from your Sleep tab — changing things in this screen won't touch your own mix.
      </Text>
    </ScrollView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  screen:   { flex: 1, backgroundColor: Colors.bg },
  bgImage:  { opacity: 0.18 },
  content:  { padding: 16, paddingTop: 20, paddingBottom: 32 },
  subheader: {
    color: Colors.textSecondary,
    ...Typography.caption,
    marginBottom: 20,
    lineHeight: 18,
  },

  // ── Section headers ────────────────────────────────────────────────────
  sectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    marginTop: 4,
  },
  sectionLabelGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  sectionIcon: { marginTop: 1 },
  sectionLabel: {
    color: Colors.textSecondary,
    ...Typography.label,
    fontFamily: Fonts.semiBold,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },

  // ── Import button ──────────────────────────────────────────────────────
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: Colors.kidsAccent,
    borderRadius: Radius.pill,
    paddingVertical: 6,
    paddingHorizontal: 12,
    minWidth: 72,
    justifyContent: 'center',
  },
  importText: {
    color: Colors.bg,
    ...Typography.small,
    fontFamily: Fonts.bold,
  },

  // ── Empty state ────────────────────────────────────────────────────────
  emptyCustom: {
    alignItems: 'center',
    paddingVertical: 28,
    paddingHorizontal: 24,
    gap: 8,
  },
  emptyIconCircle: {
    width: 52,
    height: 52,
    borderRadius: Radius.full,
    backgroundColor: Colors.kidsAccent + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  emptyTitle: {
    color: Colors.textBright,
    ...Typography.label,
    fontFamily: Fonts.semiBold,
  },
  emptyBody: {
    color: Colors.textMuted,
    ...Typography.small,
    textAlign: 'center',
    lineHeight: 17,
  },

  // ── Timer card ─────────────────────────────────────────────────────────
  timerCard: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Colors.kidsAccent + '28',
    padding: 16,
    marginTop: 20,
  },
  timerHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: 4,
  },
  timerTitle: {
    color: Colors.textBright,
    ...Typography.title,
  },
  timerSub: {
    color: Colors.textSecondary,
    ...Typography.small,
    marginBottom: 14,
    lineHeight: 17,
  },

  presetRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  presetPill: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: Radius.pill,
    backgroundColor: Colors.surface2,
  },
  presetPillActive:  { backgroundColor: Colors.kidsAccent },
  presetText:        { color: Colors.textSecondary, ...Typography.label },
  presetTextActive:  { color: Colors.bg, fontFamily: Fonts.bold },

  actionButton: {
    backgroundColor: Colors.kidsAccent,
    borderRadius: Radius.md,
    paddingVertical: 14,
    alignItems: 'center',
  },
  cancelButton:  { backgroundColor: Colors.danger },
  actionText:    { color: Colors.bg, ...Typography.button },
  cancelText:    { color: Colors.dangerText },

  note: {
    color: Colors.textMuted,
    ...Typography.small,
    marginTop: 16,
    fontStyle: 'italic',
    lineHeight: 17,
  },
});
